import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useQuery } from '@tanstack/react-query'

import CacheAPI from '../../api/orbitV1/CacheAPI'
import OrgAPI from '../../api/orbitV1/OrgAPI'
import profilePic from '../../assets/placeholders/profile_placeholder.png'
import JoyrideContext from '../../context/joyride/joyrideContext'
import {
  convertBytesToGB,
  convertMinsToHrs
} from '../../helpers/usageConverters'
import OrbitSession from '../../hooks/useCookies'
import { OnboardingTourModal } from '../Onboarding/components'
import { countSims } from '../SIMs/ManageSims/SimManagement'

import Panel from './components/Panel'

let toggledWarningFromPrevLocation = false

export default function Dashboard() {
  // reads the redirect error and toasts it to the client
  const location = useLocation()
  if (location.state?.reason && !toggledWarningFromPrevLocation) {
    toggledWarningFromPrevLocation = true
    toast.error(location.state.reason)
    delete location.state.reason
  }

  const orgId = OrbitSession.get('orgId')
  const userName = OrbitSession.get('userName')
  const firstLogin = OrbitSession.get('firstLogin')
  const [isOnboardModalOpen, setIsOnboardModalOpen] = useState(firstLogin)
  const joyrideContext = useContext(JoyrideContext)
  const { setRun, tourActive, setActive } = joyrideContext
  const teamOptions = ['Circles P&C'] // Placeholder for team
  const [team, setTeam] = useState('')
  const [bundle, setBundle] = useState({
    _id: '',
    type: '',
    sims: 0,
    price: 0,
    data: 0,
    voice: 0,
    sms: 0,
    updatedAt: new Date(
      new Date().setMinutes(new Date().getMinutes() - Math.random() * 60)
    )
  })
  const [bundleUsage, setBundleUsage] = useState({
    status: '',
    expenditure: 200,
    activatedSims: 18,
    remainingData: 200,
    remainingVoice: 200,
    remainingSms: 50,
    updatedAt: new Date(
      new Date().setMinutes(new Date().getMinutes() + Math.random() * 60)
    )
  })
  const [date, setDate] = useState('')
  const [teamMembers, setTeamMembers] = useState([])

  useEffect(() => {
    setTimeout(() => {
      OrbitSession.set({ firstLogin: false })
    }, 5000)
  }, [])

  /* useQuery to retrieve org details */
  const {
    status: orgStatus,
    data: orgDetails,
    error: orgDetailsError
  } = useQuery(['getOrgDetails', orgId], async () => {
    const { data } = await OrgAPI.getOrgDetails(orgId)
    return data
  })

  if (orgDetailsError) {
    toast.error('Error loading organization details!')
  }

  useEffect(() => {
    if (orgStatus === 'success') {
      setTeam(orgDetails[0].name)
      // setTeamMembers(orgDetails[0].users)
      OrbitSession.set({ orgName: orgDetails[0]?.name }) // Might need to hash
    }
  }, [orgDetails])

  useEffect(() => {
    // setTeam(organizationName) // To change to router link to retrieve profile name from MongoDB
    setBundle({
      _id: '000000000000000000000000',
      type: 'Circles One',
      sims: 20,
      price: 38,
      data: 200,
      voice: 200,
      sms: 50
    })
    setBundleUsage({
      status: 'Activated',
      expenditure: 200,
      activatedSims: 18,
      remainingData: 100,
      remainingVoice: 100,
      remainingSms: 40,
      createdAt: 'Fri, 15 Apr 2022 08:24:54 GMT',
      updatedAt: new Date(
        new Date().setMinutes(new Date().getMinutes() - Math.random() * 60)
      )
    })
    getTeamMembers()
    setDate('1 Nov 2021')
  }, [])

  useEffect(() => {
    if (tourActive) {
      setTimeout(() => {
        setRun(true)
      }, 500)
    }
  }, [])

  useEffect(() => {
    if (teamMembers) {
      let totalData = 0
      let totalSms = 0
      let totalVoice = 0
      let remainingData = 0
      let remainingSms = 0
      let remainingVoice = 0
      let dataApplicable = false
      let smsApplicable = false
      let voiceApplicable = false

      for (const member of teamMembers) {
        // Add member data only if his data is not unlimited
        totalData += Number(member.data) < 1e12 ? Number(member.data) : 0
        totalSms += Number(member.sms)
        // Add member voice only if his voice is not unlimited
        totalVoice += Number(member.voice) < 1e8 ? Number(member.voice) : 0
        // Add member remaining data only if his data is not unlimited
        remainingData +=
          Number(member.remainingData) < 1e12 ? Number(member.remainingData) : 0
        remainingSms += Number(member.remainingSms)
        // Only add member remainingData if his data is not unlimited
        remainingVoice +=
          Number(member.remainingVoice) < 1e8
            ? Number(member.remainingVoice)
            : 0

        // Check if fields are applicable for bundle
        dataApplicable = member.data > 0
        smsApplicable = member.sms > 0
        voiceApplicable = member.voice > 0
      }
      setBundle({
        _id: '000000000000000000000000',
        type: 'Circles One',
        sims: teamMembers.length,
        price: 38,
        data: totalData,
        voice: totalVoice,
        sms: totalSms,
        dataApplicable: Boolean(dataApplicable),
        smsApplicable: Boolean(smsApplicable),
        voiceApplicable: Boolean(voiceApplicable)
      })
      setBundleUsage({
        status: 'Activated',
        expenditure: 200,
        activatedSims: countSims('activeSims', teamMembers), // Change later when activated sims can be
        remainingData,
        remainingVoice,
        remainingSms,
        updatedAt: new Date(
          new Date().setMinutes(new Date().getMinutes() - Math.random() * 60)
        )
      })
    }
  }, [teamMembers])

  const getTeamMembers = async () => {
    try {
      const cachedOrgDetails = await CacheAPI.getCachedOrgDetails(orgId)
      if (
        cachedOrgDetails &&
        cachedOrgDetails.data &&
        cachedOrgDetails.data.length > 0
      ) {
        setTeamMembers(cachedOrgDetails.data)
      } else {
        toast.error(
          'We are unable access your team data at the moment, please try again later'
        )
      }
    } catch (error) {
      toast.error(error.response.message)
    }
  }

  const handleStartOnboarding = async () => {
    setRun(true)
    setActive(true)
    setIsOnboardModalOpen(false)
  }

  return (
    <>
      {firstLogin && (
        <OnboardingTourModal
          isOnboardModalOpen={isOnboardModalOpen}
          setIsOnboardModalOpen={setIsOnboardModalOpen}
          userName={userName}
          handleStartOnboarding={handleStartOnboarding}
        />
      )}
      <Panel
        team={team}
        teamMembers={teamMembers}
        bundleUsage={bundleUsage}
        bundle={bundle}
        convertData={convertBytesToGB}
        convertVoice={convertMinsToHrs}
        teamOptions={teamOptions}
        setTeam={setTeam}
        date={date}
        profilePic={profilePic}
      />
    </>
  )
}
